import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { ItineraryService } from './itinerary.service';
import { ItineraryItem, ItemCategory, CATEGORIES } from './itinerary.model';

@Component({
    selector: 'app-itinerary',
    templateUrl: './itinerary.component.html',
    styleUrls: ['./itinerary.component.scss'],
    standalone: false
})
export class ItineraryComponent implements OnInit, OnDestroy {
  items: ItineraryItem[] = [];
  groupedDays: { date: string; label: string; items: ItineraryItem[] }[] = [];
  undatedItems: ItineraryItem[] = [];
  collapsedDays: Set<string> = new Set();

  showItemModal = false;
  selectedItem: ItineraryItem | null = null;

  showFormModal = false;
  editingItemId: string | null = null;
  newItem: Partial<ItineraryItem> = {};
  formError = '';

  draggingItem: ItineraryItem | null = null;
  dragOverDate: string | null = null;
  dragOverUnscheduled = false;

  categories = CATEGORIES;

  private itemsSub?: Subscription;

  constructor(private itineraryService: ItineraryService) {}

  get showRightPanel(): boolean {
    return !!this.selectedItem || this.undatedItems.length > 0;
  }

  ngOnInit() {
    this.itemsSub = this.itineraryService.getItems().subscribe(items => {
      this.items = items.sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;
        if (!a.time) return 1;
        if (!b.time) return -1;
        return a.time.localeCompare(b.time);
      });
      this.groupedDays = this.computeGroupedDays();
      this.undatedItems = this.items.filter(item => !item.date);

      // Keep selectedItem in sync when Firebase updates
      if (this.selectedItem) {
        this.selectedItem = this.items.find(i => i.id === this.selectedItem!.id) || null;
      }
    });
    this.resetForm();
  }

  ngOnDestroy() {
    this.itemsSub?.unsubscribe();
  }

  private computeGroupedDays(): { date: string; label: string; items: ItineraryItem[] }[] {
    const dayMap = new Map<string, ItineraryItem[]>();
    this.items.filter(item => !!item.date).forEach(item => {
      if (!dayMap.has(item.date!)) dayMap.set(item.date!, []);
      dayMap.get(item.date!)!.push(item);
    });
    return Array.from(dayMap.entries()).map(([date, dayItems]) => ({
      date,
      label: new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      }),
      items: dayItems
    }));
  }

  toggleDay(date: string) {
    if (this.collapsedDays.has(date)) {
      this.collapsedDays.delete(date);
    } else {
      this.collapsedDays.add(date);
    }
  }

  isDayCollapsed(date: string): boolean {
    return this.collapsedDays.has(date);
  }

  getCategoryConfig(category: ItemCategory | undefined) {
    return CATEGORIES.find(c => c.value === category) || null;
  }

  formatTime(time: string | undefined): string {
    if (!time) return '';
    const [h, m] = time.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return time;
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${m.toString().padStart(2, '0')} ${period}`;
  }

  openItemModal(item: ItineraryItem) {
    this.selectedItem = item;
    this.showItemModal = true;
  }

  closeItemModal() {
    this.showItemModal = false;
    this.selectedItem = null;
  }

  openFormModal() {
    this.showFormModal = true;
  }

  openFormModalForDay(date: string) {
    this.resetForm();
    this.newItem = { date };
    this.showFormModal = true;
  }

  openEditModal(item: ItineraryItem) {
    this.closeItemModal();
    this.editingItemId = item.id;
    this.newItem = { ...item };
    this.formError = '';
    this.showFormModal = true;
  }

  closeFormModal() {
    this.showFormModal = false;
    this.resetForm();
  }

  resetForm() {
    this.editingItemId = null;
    this.newItem = {};
    this.formError = '';
  }

  saveItem() {
    this.formError = '';
    if (!this.newItem.name) {
      this.formError = 'Name is required.';
      return;
    }

    const item: ItineraryItem = {
      id: this.editingItemId || uuidv4(),
      name: this.newItem.name,
      ...(this.newItem.date && { date: this.newItem.date }),
      ...(this.newItem.time && { time: this.newItem.time }),
      ...(this.newItem.category && { category: this.newItem.category }),
      ...(this.newItem.link && { link: this.newItem.link }),
      ...(this.newItem.notes && { notes: this.newItem.notes }),
      ...(this.newItem.location && { location: this.newItem.location }),
      ...(this.newItem.cost != null && { cost: this.newItem.cost }),
    };

    const savePromise = this.editingItemId
      ? this.itineraryService.updateItem(item)
      : this.itineraryService.addItem(item);

    savePromise.then(() => {
      this.closeFormModal();
    }).catch(() => {
      this.formError = 'Failed to save. Please try again.';
    });
  }

  deleteItem(item: ItineraryItem) {
    if (confirm(`Delete "${item.name}"?`)) {
      this.itineraryService.deleteItem(item.id).then(() => {
        this.closeItemModal();
      }).catch(() => {
        alert('Failed to delete. Please try again.');
      });
    }
  }

  // Drag and drop
  onDragStart(item: ItineraryItem) {
    this.draggingItem = item;
  }

  onDragEnd() {
    this.draggingItem = null;
    this.dragOverDate = null;
    this.dragOverUnscheduled = false;
  }

  onDragOver(event: DragEvent, date: string) {
    event.preventDefault();
    this.dragOverDate = date;
  }

  onDrop(event: DragEvent, date: string) {
    event.preventDefault();
    if (this.draggingItem) {
      const updated: ItineraryItem = { ...this.draggingItem, date };
      this.itineraryService.updateItem(updated).catch(() => {
        alert('Failed to schedule item. Please try again.');
      });
    }
    this.draggingItem = null;
    this.dragOverDate = null;
  }

  onDragOverUnscheduled(event: DragEvent) {
    event.preventDefault();
    this.dragOverUnscheduled = true;
  }

  onDropUnscheduled(event: DragEvent) {
    event.preventDefault();
    if (this.draggingItem && this.draggingItem.date) {
      const { date: _removed, ...rest } = this.draggingItem;
      this.itineraryService.updateItem(rest as ItineraryItem).catch(() => {
        alert('Failed to unschedule item. Please try again.');
      });
    }
    this.draggingItem = null;
    this.dragOverUnscheduled = false;
  }
}
